import type { TLRichText } from "@tldraw/tlschema";

function importedColor(value: string) {
  const color = value.trim().toLowerCase();
  return /^(?:#[\da-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%degturnrad]+\)|(?:black|white|gray|grey|red|green|blue|orange|yellow|purple|brown|navy|teal|maroon))$/i.test(
    color,
  )
    ? color
    : "";
}

function importedAlignment(element: Element) {
  const value = (
    (element instanceof HTMLElement && element.style.textAlign) ||
    element.getAttribute("align") ||
    ""
  ).toLowerCase();
  return ["left", "center", "right", "justify"].includes(value) ? value : null;
}

/** Convert only known formatting into editor JSON. Never mount imported HTML. */
export function importedRichText(html: string): TLRichText {
  const doc = new DOMParser().parseFromString(html, "text/html");
  type Node = {
    type: string;
    text?: string;
    attrs?: Record<string, unknown>;
    marks?: { type: string; attrs?: Record<string, unknown> }[];
    content?: Node[];
  };
  const inline = (
    node: globalThis.Node,
    marks: NonNullable<Node["marks"]> = [],
  ): Node[] => {
    if (node.nodeType === 3)
      return node.textContent
        ? [
            {
              type: "text",
              text: node.textContent,
              ...(marks.length ? { marks } : {}),
            },
          ]
        : [];
    if (
      !(node instanceof Element) ||
      ["SCRIPT", "STYLE", "IFRAME", "OBJECT"].includes(node.tagName)
    )
      return [];
    if (node.tagName === "BR") return [{ type: "hardBreak" }];
    if (node.tagName === "IMG")
      return [
        {
          type: "text",
          text: `[Image: ${node.getAttribute("alt") || "see original document"}]`,
        },
      ];
    const mark = (
      {
        B: "bold",
        STRONG: "bold",
        I: "italic",
        EM: "italic",
        U: "underline",
        S: "strike",
      } as Record<string, string>
    )[node.tagName];
    const next = mark ? [...marks, { type: mark }] : [...marks];
    if (node instanceof HTMLElement) {
      if (
        node.style.fontWeight === "bold" ||
        Number(node.style.fontWeight) >= 600
      )
        next.push({ type: "bold" });
      if (node.style.fontStyle === "italic") next.push({ type: "italic" });
      if (node.style.textDecoration.includes("underline"))
        next.push({ type: "underline" });
      const color = importedColor(node.style.color);
      if (color) next.push({ type: "textStyle", attrs: { color } });
    }
    if (
      node.tagName === "A" &&
      /^(https?:|mailto:)/i.test(node.getAttribute("href") || "")
    )
      next.push({
        type: "link",
        attrs: {
          href: node.getAttribute("href"),
          target: "_blank",
          rel: "noopener noreferrer",
        },
      });
    const unique = next.filter(
      (candidate, index) =>
        next.findIndex(
          (other) =>
            other.type === candidate.type &&
            JSON.stringify(other.attrs) === JSON.stringify(candidate.attrs),
        ) === index,
    );
    return [...node.childNodes].flatMap((n) => inline(n, unique));
  };
  const blocks = (parent: Element): Node[] =>
    [...parent.children].flatMap((el): Node[] => {
      if (["SCRIPT", "STYLE", "IFRAME", "OBJECT"].includes(el.tagName))
        return [];
      if (el.tagName === "UL" || el.tagName === "OL")
        return [
          {
            type: el.tagName === "UL" ? "bulletList" : "orderedList",
            content: [...el.children]
              .filter((n) => n.tagName === "LI")
              .map((li) => ({
                type: "listItem",
                content: [
                  {
                    type: "paragraph",
                    content: [...li.childNodes]
                      .filter(
                        (n) =>
                          !(
                            n instanceof Element &&
                            ["UL", "OL"].includes(n.tagName)
                          ),
                      )
                      .flatMap((n) => inline(n)),
                  },
                  ...blocks(li).filter((n) => n.type.endsWith("List")),
                ],
              })),
          },
        ];
      if (/^H[1-6]$/.test(el.tagName))
        return [
          {
            type: "heading",
            attrs: {
              level: Number(el.tagName[1]),
              ...(importedAlignment(el)
                ? { textAlign: importedAlignment(el) }
                : {}),
            },
            content: inline(el),
          },
        ];
      if (el.tagName === "TABLE")
        return [...el.querySelectorAll("tr")].map((tr) => ({
          type: "paragraph",
          content: [
            {
              type: "text",
              text: [...tr.children]
                .map((c) => c.textContent ?? "")
                .join("  |  "),
            },
          ],
        }));
      if (el.tagName === "DIV" && el.children.length) return blocks(el);
      return [
        {
          type: "paragraph",
          ...(importedAlignment(el)
            ? { attrs: { textAlign: importedAlignment(el) } }
            : {}),
          content: inline(el),
        },
      ];
    });
  const content = blocks(doc.body);
  return {
    type: "doc",
    content: content.length
      ? content
      : [{ type: "paragraph", content: inline(doc.body) }],
  } as TLRichText;
}
